import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import GTLogo from "../../Images/logo.png";
import { motion, radius } from "../../theme/tokens";
import { moduleAccent, ModuleKey } from "./moduleAccent";

/**
 * The GT Brokers loading system.
 *
 * One component, three variants, used everywhere the portal waits:
 *
 *   page     app initialisation, auth resolution, lazy route chunks
 *   section  a card, panel or table surface that genuinely cannot render yet
 *   inline   a button or small control mid-action
 *
 * The page variant is a brand panel: the existing logo, a four-step insurance
 * metaphor, an indeterminate rail, and the label. The metaphor —
 *
 *   CLIENT -> POLICY -> COVERAGE -> PROTECTED
 *
 * — is what makes a wait feel like this product rather than like any admin
 * template with a spinner in the middle. It is decorative: the steps do NOT
 * correspond to backend work, nothing is being processed in that order, and
 * the highlight simply cycles while the real request is in flight. That is
 * exactly why the whole sequence is `aria-hidden` and only the label is
 * announced — a screen reader hears "Loading clients", once, not four
 * meaningless nouns on repeat.
 *
 * Timing honesty: there is no minimum display duration anywhere in here. If
 * the request resolves in 180ms the loader is gone in 180ms, mid-animation.
 * Nothing waits for a cycle to complete.
 *
 * The brand mark is the existing `src/Images/logo.png` — the same import the
 * sidebar and login screen already use, so the build hashes one asset and no
 * new image was created. It never spins: it fades up once and holds still.
 *
 * Everything animating is CSS on top of MUI. No animation library was added.
 * Under `prefers-reduced-motion` the cycle stops on the first step and the
 * rail holds a static filled bar — still legibly a loading state, just not a
 * moving one.
 */
export type AppLoaderVariant = "page" | "section" | "inline";

export interface AppLoaderProps {
  variant?: AppLoaderVariant;
  /** Announced to assistive tech and shown beneath the panel. */
  label?: string;
  /** Override the reserved height. Page defaults to the viewport. */
  height?: number | string;
  /** Inline only: diameter in px. */
  size?: number;
  /** Page only: paint the themed page background behind the panel. */
  fullBleed?: boolean;
  /**
   * Tints the active step and the rail with a module's accent. The structure
   * is identical for every module — this is one loader, not eight.
   */
  module?: ModuleKey;
  /**
   * Inline only: draw the ring in the parent's own text colour instead of the
   * brand accent. Used inside a filled button, where a brand-blue ring on a
   * brand-blue ground would be invisible.
   */
  inherit?: boolean;
}

/** One visual stage per ~750ms; the four together make a 3s cycle. */
const STAGE_MS = 750;
const CYCLE_MS = STAGE_MS * 4;
const RAIL_MS = 2000;
const ENTRANCE = 420;

const STEPS = [
  { key: "client", label: "Client", Icon: PersonOutlineRoundedIcon },
  { key: "policy", label: "Policy", Icon: DescriptionOutlinedIcon },
  { key: "coverage", label: "Coverage", Icon: ShieldOutlinedIcon },
  { key: "protected", label: "Protected", Icon: VerifiedUserRoundedIcon },
] as const;

const AppLoader = ({
  variant = "page",
  label = "Loading...",
  height,
  size = 18,
  fullBleed = true,
  module,
  inherit = false,
}: AppLoaderProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = moduleAccent(theme, module ?? "dashboard");
  const brand = accent.main;
  const pair = accent.pair;

  /* ------------------------------------------------------------------ */
  /* inline — buttons and small controls                                 */
  /* ------------------------------------------------------------------ */
  if (variant === "inline") {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-label={label}
        sx={{ display: "inline-flex", alignItems: "center", gap: 1, lineHeight: 1 }}
      >
        <Box
          aria-hidden
          sx={{
            width: size,
            height: size,
            flexShrink: 0,
            borderRadius: "50%",
            /* `currentColor` picks up the button's own text colour, so the
               ring stays visible on a filled brand-blue CTA. */
            border: `2px solid ${
              inherit ? alpha(theme.palette.common.white, 0.32) : alpha(brand, isDark ? 0.28 : 0.2)
            }`,
            borderTopColor: inherit ? "currentColor" : brand,
            borderRightColor: inherit ? "currentColor" : alpha(pair, 0.9),
            animation: `gtLoaderSpin 900ms linear infinite`,
            "@keyframes gtLoaderSpin": { to: { transform: "rotate(360deg)" } },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              borderTopColor: brand,
            },
          }}
        />
        {label && label !== "Loading..." && (
          <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
            {label}
          </Box>
        )}
      </Box>
    );
  }

  const isPage = variant === "page";

  /* Section is a materially smaller version of the same thing — a table that
     is refreshing must not be handed a 380px brand panel. */
  const panelWidth = isPage ? { xs: 292, sm: 336, md: 364 } : { xs: 260, md: 300 };
  const logoWidth = isPage ? { xs: 132, sm: 150 } : { xs: 104, md: 116 };
  const tile = isPage ? 38 : 32;
  const iconSize = isPage ? 19 : 16;

  /**
   * Per-step keyframes.
   *
   * Each step owns its own keyframe set rather than sharing one with a
   * negative `animation-delay`. Shared-keyframes-plus-delay looks tidier but
   * shows the wrong frame before its delay elapses, so the first cycle lights
   * every step at once. Four small keyframe sets are the reliable version.
   */
  const stepKeyframes = (index: number) => {
    const start = index * 25;
    const end = start + 25;
    const activeStop = {
      color: accent.ink,
      backgroundColor: alpha(brand, isDark ? 0.22 : 0.12),
      borderColor: alpha(brand, isDark ? 0.44 : 0.32),
      transform: "translateY(-1px)",
    };
    const idleStop = {
      color: alpha(theme.palette.text.primary, isDark ? 0.5 : 0.45),
      backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.07 : 0.05),
      borderColor: alpha(theme.palette.divider, isDark ? 0.45 : 0.7),
      transform: "none",
    };
    /* Held for the step's quarter of the cycle, idle either side. */
    return {
      "0%": index === 0 ? activeStop : idleStop,
      [`${Math.max(start - 0.01, 0)}%`]: idleStop,
      [`${start}%`]: activeStop,
      [`${end - 0.01}%`]: activeStop,
      [`${end}%`]: idleStop,
      "100%": idleStop,
    } as Record<string, object>;
  };

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        /* Reserving height stops the surface collapsing and the page jumping
           when real content replaces the loader. */
        minHeight: height ?? (isPage ? "100vh" : 300),
        px: 2,
        py: isPage ? 4 : 3,
        textAlign: "center",
        ...(isPage &&
          fullBleed && {
            backgroundColor: theme.palette.background.default,
            /* One soft radial behind the panel — no image, no banding. */
            backgroundImage: `radial-gradient(680px 420px at 50% 42%, ${alpha(
              brand,
              isDark ? 0.16 : 0.1
            )} 0%, transparent 70%)`,
          }),
      }}
    >
      {/* ---------------------------------------------------------------- */}
      {/* Brand panel                                                       */}
      {/* ---------------------------------------------------------------- */}
      <Box
        sx={{
          width: "100%",
          maxWidth: panelWidth,
          px: { xs: 2.25, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          borderRadius: `${radius.xxl}px`,
          backgroundColor: theme.palette.cardAdd.bgColor,
          border: `1px solid ${alpha(brand, isDark ? 0.26 : 0.16)}`,
          boxShadow: isDark
            ? `0 2px 6px ${alpha("#000", 0.45)}, 0 24px 56px -24px ${alpha("#000", 0.8)}`
            : `0 1px 3px ${alpha("#0f2744", 0.06)}, 0 24px 56px -28px ${alpha("#0f2744", 0.4)}`,
          animation: `gtPanelIn ${ENTRANCE}ms cubic-bezier(0.22, 1, 0.36, 1) both`,
          "@keyframes gtPanelIn": {
            from: { opacity: 0, transform: "translateY(8px)" },
            to: { opacity: 1, transform: "none" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        {/* The mark. Static — brand logos do not spin. */}
        <Box
          component="img"
          src={GTLogo}
          alt="GT Brokers"
          sx={{
            width: logoWidth,
            maxWidth: "78%",
            height: "auto",
            objectFit: "contain",
            display: "block",
            mx: "auto",
          }}
        />

        {/* ------------------------------------------------------------ */}
        {/* Insurance sequence — decorative, hidden from assistive tech.  */}
        {/* ------------------------------------------------------------ */}
        <Box
          aria-hidden
          sx={{
            mt: isPage ? 2.75 : 2.25,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: { xs: 0.25, sm: 0.5 },
          }}
        >
          {STEPS.map((step, index) => (
            <React.Fragment key={step.key}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.6,
                  minWidth: 0,
                  flex: "0 0 auto",
                }}
              >
                <Box
                  sx={{
                    width: tile,
                    height: tile,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: `${radius.md}px`,
                    border: "1px solid",
                    transition: `color ${motion.fast}ms, background-color ${motion.fast}ms`,
                    animation: `gtStep${index} ${CYCLE_MS}ms ease-in-out infinite`,
                    [`@keyframes gtStep${index}`]: stepKeyframes(index),
                    "& .MuiSvgIcon-root": { fontSize: iconSize },
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none",
                      /* Static: first step reads active, the rest idle. */
                      color: index === 0 ? accent.ink : alpha(theme.palette.text.primary, 0.45),
                      backgroundColor:
                        index === 0
                          ? alpha(brand, isDark ? 0.22 : 0.12)
                          : alpha(theme.palette.text.primary, isDark ? 0.07 : 0.05),
                      borderColor:
                        index === 0
                          ? alpha(brand, isDark ? 0.44 : 0.32)
                          : alpha(theme.palette.divider, isDark ? 0.45 : 0.7),
                    },
                  }}
                >
                  <step.Icon />
                </Box>
                <Typography
                  sx={{
                    fontSize: isPage ? 9.5 : 9,
                    fontWeight: 700,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    color: alpha(theme.palette.text.primary, isDark ? 0.66 : 0.6),
                  }}
                >
                  {step.label}
                </Typography>
              </Box>

              {/* Connector between tiles, not after the last one. */}
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    width: { xs: 8, sm: 14 },
                    height: 1,
                    mt: `${tile / 2}px`,
                    flex: "0 0 auto",
                    backgroundColor: alpha(theme.palette.divider, isDark ? 0.5 : 0.85),
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* ------------------------------------------------------------ */}
        {/* Indeterminate rail. No percentage — there is no real progress  */}
        {/* to report, and inventing one would be a lie about the wait.   */}
        {/* ------------------------------------------------------------ */}
        <Box
          aria-hidden
          sx={{
            position: "relative",
            mt: isPage ? 2.75 : 2.25,
            mx: "auto",
            width: "100%",
            height: 3,
            borderRadius: `${radius.pill}px`,
            overflow: "hidden",
            backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.16 : 0.1),
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              width: "40%",
              borderRadius: `${radius.pill}px`,
              background: `linear-gradient(90deg, ${alpha(brand, 0.15)}, ${brand}, ${pair})`,
              animation: `gtLoaderSweep ${RAIL_MS}ms ${motion.easing} infinite`,
            },
            "@keyframes gtLoaderSweep": {
              "0%": { transform: "translateX(-105%)" },
              "100%": { transform: "translateX(255%)" },
            },
            "@media (prefers-reduced-motion: reduce)": {
              "&::after": { animation: "none", width: "100%", opacity: 0.75 },
            },
          }}
        />
      </Box>

      {label && (
        <Typography
          sx={{
            mt: 1.75,
            fontSize: isPage ? 13.5 : 12.5,
            fontWeight: 600,
            letterSpacing: 0.2,
            color: isDark ? theme.palette.heading.headingColor : theme.palette.text.secondary,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default AppLoader;
